import { useState } from 'react';
import { Tag, Tooltip, Space, Popover, InputNumber, Button, Typography, message } from 'antd';
import { TrophyOutlined, StarOutlined, GiftOutlined } from '@ant-design/icons';
import { usePosCustomerTier, usePosLoyaltyBalance, usePosLoyaltyProgram } from '@/hooks/usePromotions';

const { Text } = Typography;

export interface LoyaltyRedeemResult {
  points: number;
  value: number;
}

interface Props {
  customerId: string;
  /** Callback when points are successfully redeemed. */
  onRedeem?: (result: LoyaltyRedeemResult) => void;
  /** Currently redeemed points (to show chip). */
  redeemedPoints?: number;
  redeemedValue?: number;
  /**
   * FINAL-C1 (final review) — số điểm ĐÃ LƯU trên đơn (`savedRedemption.points`
   * ở `CustomerSidebar/index.tsx`), KHÁC HẲN `redeemedPoints` — sau các vòng
   * review trước, `redeemedPoints` đã đổi nghĩa thành giá trị LIVE (số người
   * dùng vừa chọn trong phiên, CHƯA lưu). Dùng `redeemedPoints` để tính trần
   * sẽ tự nới trần theo đúng số đang gõ (vô nghĩa). `heldPoints` PHẢI lấy từ
   * nguồn snapshot đơn (`savedRedemption`), đứng yên suốt phiên sửa bất kể
   * người dùng gõ/xác nhận bao nhiêu lần.
   *
   * Lý do cần: điểm của đơn đã bị TRỪ khỏi số dư (`usePosLoyaltyBalance`)
   * ngay lúc TẠO đơn — số dư khả dụng để SỬA lại đơn này phải CỘNG THÊM số
   * điểm đơn đang giữ (backend hoàn trước, trừ sau, cùng transaction), không
   * phải số dư trừ suông. Thiếu field này: khách 12.000 điểm đã đổi 10.000
   * cho đơn (dư còn 2.000) muốn sửa xuống 8.000 sẽ KHÔNG bấm được vì
   * `8.000 > 2.000`.
   */
  heldPoints?: number;
  /**
   * TCR-1353 — `'create'` (mặc định) giữ nguyên hành vi màn POS: ô nhập luôn
   * khởi tạo bằng 0. `'edit'` dùng cho màn sửa đơn: ô nhập khởi tạo bằng số
   * điểm đơn ĐANG dùng (`redeemedPoints`) thay vì 0, vì ở đây người dùng đang
   * sửa một con số có sẵn chứ không nhập mới từ đầu.
   */
  mode?: 'create' | 'edit';
  /**
   * Cho phép bấm "Dùng điểm" hay không. Mặc định `undefined` (coi như cho
   * phép) để KHÔNG đổi hành vi màn POS hiện tại. Màn sửa đơn truyền `false`
   * khi đơn không ở trạng thái được sửa (nháp/chờ xác nhận) — trạng thái khác
   * backend từ chối đổi điểm, hiện nút ra là mời người dùng vào một thao tác
   * chắc chắn lỗi.
   */
  editable?: boolean;
}

export function LoyaltyBadge({
  customerId,
  onRedeem,
  redeemedPoints,
  redeemedValue,
  heldPoints,
  mode = 'create',
  editable,
}: Props) {
  const { data: points } = usePosLoyaltyBalance(customerId);
  const { data: program } = usePosLoyaltyProgram();
  const { data: customerTier } = usePosCustomerTier(customerId);

  const [redeemOpen, setRedeemOpen] = useState(false);
  // TCR-1353: khởi tạo 0 lúc mount, KHÔNG đọc `redeemedPoints` ở đây — Task 11
  // sẽ fetch số điểm đã chốt bất đồng bộ (gọi API theo đơn), gần như chắc
  // chắn dữ liệu tới SAU lần render đầu. Đọc ở `useState` initializer sẽ kẹt
  // ô nhập ở 0 mãi mãi vì initializer chỉ chạy một lần. Giá trị đúng được nạp
  // lại mỗi lần MỞ popover, xem `handleOpenChange` bên dưới.
  const [redeemPoints, setRedeemPoints] = useState<number>(0);

  if (points == null) return null;

  const tier = customerTier?.tier?.name;
  const redemptionRate = program?.redemption?.rate ?? 1_000;
  const minRedeem = program?.redemption?.min_points ?? 0;

  const estimatedValue = redeemPoints * redemptionRate;

  /**
   * FINAL-C1 (final review) — trần đổi điểm KHẢ DỤNG cho đơn đang sửa, KHÔNG
   * PHẢI số dư ví hiện tại (`points`). Điểm của chính đơn này đã bị trừ khỏi
   * `points` từ lúc TẠO đơn — sửa lại đơn phải CỘNG lại đúng số đó vào trần,
   * đối xứng với backend (hoàn trước, trừ sau, cùng transaction). Mode
   * 'create' không có khái niệm "điểm đang giữ trên đơn" (đơn chưa tồn tại)
   * nên `available === points`, giữ NGUYÊN hành vi cũ.
   */
  const available = mode === 'edit' ? points + (heldPoints ?? 0) : points;

  /**
   * Review (a) — mode 'edit' cho phép xác nhận với `redeemPoints === 0` để
   * GỠ HẾT điểm khỏi đơn (backend, Task 6: `loyalty_redeem_points: 0` là lệnh
   * gỡ hết, hợp lệ). Mode 'create' (màn POS tạo đơn) giữ NGUYÊN hành vi cũ —
   * 0 vẫn coi là "chưa nhập gì", không có khái niệm "gỡ" cho một đơn chưa tồn
   * tại điểm nào để gỡ.
   */
  const isRemovingAll = mode === 'edit' && redeemPoints === 0;
  const redeemDisabled =
    redeemPoints > available || (!isRemovingAll && (!redeemPoints || redeemPoints <= 0));

  // TCR-1353: CHỈ nạp lại `redeemedPoints` khi `mode === 'edit'`, và chỉ lúc
  // MỞ popover (không chỉ lúc mount) — closure này đóng theo props hiện tại
  // của lần render gần nhất nên luôn thấy giá trị mới nhất kể cả khi nó tới
  // muộn sau lần render đầu (Task 11 fetch API riêng theo đơn).
  //
  // Review fix (Minor, regression màn POS): TUYỆT ĐỐI không đụng `redeemPoints`
  // khi `mode === 'create'`. Ở màn tạo đơn (POS), số đang gõ là NHÁP CỦA
  // CHÍNH HỌ — thu ngân gõ dở, lỡ click ra ngoài đóng popover, mở lại thì số
  // đó phải còn nguyên như trước Task 10, không được reset về 0/redeemedPoints.
  //
  // Re-review (Minor) — comment CŨ ở đây từng nói "ở màn sửa đơn, mở popover
  // phải LUÔN hiện lại số đã CHỐT trên đơn (bỏ nháp)" — KHÔNG CÒN ĐÚNG sau
  // review (b) (`CustomerSidebar/index.tsx`): `redeemedPoints` giờ ưu tiên
  // giá trị LIVE trên form (`loyaltyRedeemPoints ?? savedRedemption?.points`)
  // — nếu người dùng ĐÃ xác nhận một số MỚI trong phiên sửa hiện tại, mở lại
  // popover sẽ nạp lại đúng số MỚI đó (CHƯA lưu), không phải số đã chốt cũ
  // trên đơn. Chỉ khi CHƯA đụng gì tới ô điểm (`loyaltyRedeemPoints` vẫn
  // `undefined`) thì `redeemedPoints` mới rơi về số đã chốt như mô tả ban đầu.
  const handleOpenChange = (open: boolean) => {
    setRedeemOpen(open);
    if (open && mode === 'edit') {
      setRedeemPoints(redeemedPoints ?? 0);
    }
  };

  const handleRedeem = () => {
    if (redeemDisabled) return;
    if (redeemPoints > available) {
      void message.warning('Số điểm vượt quá số dư');
      return;
    }
    // Review (a) — mức tối thiểu (`minRedeem`) CHỈ áp khi số mới > 0. Backend
    // (Task 6) cũng chỉ kiểm mức tối thiểu ở nhánh có đổi điểm thật; gỡ hết về
    // 0 không phải "đổi điểm dưới mức tối thiểu", chặn nó bằng `minRedeem` sẽ
    // khoá luôn đường gỡ điểm cho MỌI đơn có mức tối thiểu > 0.
    if (!isRemovingAll && minRedeem > 0 && redeemPoints < minRedeem) {
      void message.warning(`Tối thiểu ${minRedeem.toLocaleString('vi-VN')} điểm`);
      return;
    }
    // Record intent locally — actual redemption happens on BE during order creation
    const value = estimatedValue;
    if (isRemovingAll) {
      void message.success('Đã gỡ điểm tích luỹ khỏi đơn');
    } else {
      void message.success(`Sẽ dùng ${redeemPoints.toLocaleString('vi-VN')} điểm = ${value.toLocaleString('vi-VN')}₫ khi thanh toán`);
    }
    onRedeem?.({ points: redeemPoints, value });
    setRedeemOpen(false);
    setRedeemPoints(0);
  };

  const redeemContent = (
    <div style={{ width: 240, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Text strong>Dùng điểm tích lũy</Text>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
        <Text type="secondary">Số dư hiện tại:</Text>
        <Text strong>{points.toLocaleString('vi-VN')} điểm</Text>
      </div>
      {minRedeem > 0 && (
        <div style={{ fontSize: 11, color: '#999' }}>
          Tối thiểu: {minRedeem.toLocaleString('vi-VN')} điểm
        </div>
      )}
      <InputNumber
        style={{ width: '100%' }}
        min={0}
        max={available}
        value={redeemPoints}
        onChange={(v) => setRedeemPoints(v ?? 0)}
        placeholder="Nhập số điểm"
        addonAfter="điểm"
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
        <Text type="secondary">Quy đổi ({redemptionRate.toLocaleString('vi-VN')}₫/điểm):</Text>
        <Text strong style={{ color: '#a4193d' }}>{estimatedValue.toLocaleString('vi-VN')}₫</Text>
      </div>
      <Button
        type="primary"
        size="small"
        block
        disabled={redeemDisabled}
        onClick={handleRedeem}
        style={{ background: '#a4193d', borderColor: '#a4193d' }}
      >
        {isRemovingAll ? 'Gỡ hết điểm' : 'Xác nhận dùng điểm'}
      </Button>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
      <Space size={4}>
        <Tooltip title={`${points.toLocaleString('vi-VN')} điểm tích lũy`}>
          <Tag icon={<StarOutlined />} color="gold" style={{ margin: 0 }}>
            {points.toLocaleString('vi-VN')} điểm
          </Tag>
        </Tooltip>
        {tier && (
          <Tag icon={<TrophyOutlined />} color="purple" style={{ margin: 0 }}>
            {tier}
          </Tag>
        )}
        {/*
          Review (a) + FINAL-C1 — mode 'edit' vẫn phải mở được popover dù
          `points` (số dư VÍ hiện tại) đã về 0, MIỄN LÀ `available` (ví +
          điểm đơn đang giữ) còn > 0 — ca khách tiêu hết số dư SAU KHI đã đổi
          điểm cho đơn này: không có đường vào thì không gỡ lại được khoản đã
          đổi, điểm bị KẸT vĩnh viễn trên đơn.

          Bản ĐẦU của gate này dùng `redeemedPoints` (giá trị LIVE) thay vì
          `heldPoints` (giá trị ĐÃ LƯU) — lỗi CÙNG LỚP với `redeemDisabled`:
          sau khi người dùng bấm "Gỡ hết điểm" (`redeemedPoints` LIVE về 0
          ngay trong phiên, TRƯỚC khi lưu), gate đó sập lại NGAY, không mở
          lại popover để đổi ý được nữa dù đơn (trên server) vẫn đang giữ
          nguyên số điểm cũ. `available` dùng `heldPoints` (đứng yên suốt
          phiên) nên không dính lỗi này — mode 'create' có `available ===
          points`, hành vi giữ nguyên.
        */}
        {available > 0 && editable !== false && (
          <Popover
            content={redeemContent}
            trigger="click"
            open={redeemOpen}
            onOpenChange={handleOpenChange}
            placement="bottomLeft"
          >
            <Tag
              icon={<GiftOutlined />}
              color="red"
              style={{ margin: 0, cursor: 'pointer' }}
            >
              Dùng điểm
            </Tag>
          </Popover>
        )}
      </Space>
      {/* Chip showing redeemed points */}
      {redeemedPoints != null && redeemedPoints > 0 && (
        <Tag color="green" style={{ margin: 0, fontSize: 11 }}>
          Đã dùng {redeemedPoints.toLocaleString('vi-VN')} điểm = {(redeemedValue ?? 0).toLocaleString('vi-VN')}₫
        </Tag>
      )}
    </div>
  );
}
